import styled, { css } from 'styled-components';
import { fieldFocusRing } from '../shared/boxedField';
import { withCssSelector } from '../shared/withCssSelector';
import { transition } from '../themes/mainTheme';

export type CardOptionOrientation = 'vertical' | 'horizontal';
export type CardOptionSize = 'default' | 'large';

// A described card reads as title + body rather than as one label, so the two
// lines get their own stack with a tighter gap than the card's own.
const StyledBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	text-align: left;
`;

// Title takes the card's own colour so it brightens with everything else.
const StyledTitle = styled.div`
	font-weight: 600;
`;

const StyledDescription = styled.div``;

// Bleeds to the card's border rather than sitting inside its padding, which is
// why an imaged card moves that padding onto the text block below.
const StyledImage = styled.img`
	display: block;
	width: 100%;
`;

const StyledCard = styled.div<{
	$selected?: boolean,
	$disabled?: boolean,
	$orientation?: CardOptionOrientation,
	$size?: CardOptionSize,
	$hasImage?: boolean,
}>`
	font-family: ${props => props.theme.font};
	color: ${props => props.theme.card.color};
	padding: 8px 12px;
	border-radius: 2px;
	font-size: 14px;
	border: 1px solid ${props => props.theme.card.default.border};
	cursor: pointer;
	// Transparent like a boxed field: the card shows whatever surface it sits on
	// instead of painting a second one on top of it.
	background-color: transparent;
	transition: ${transition('border-color', 'box-shadow')};
	line-height: 20px;
	min-height: 36px;
	display: flex;
	flex-direction: column;
	gap: 10px;
	align-items: center;
	justify-content: center;

	// A graphic reads as an icon beside the label rather than a picture above it.
	${({ $orientation }) => $orientation === 'horizontal' && css`
		flex-direction: row;
		gap: 8px;
		justify-content: flex-start;
	`}

	// Large is the form-page card: full width, content packed into the top-left
	// corner, graphic on its own row above the text.
	${({ $size }) => $size === 'large' && css`
		flex: 1 1 0;
		min-width: 0;
		padding: 24px;
		gap: 16px;
		align-items: flex-start;
		justify-content: flex-start;
		text-align: left;

		${StyledTitle} {
			font-size: 16px;
		}
	`}

	// Description sits one step below the label, and both move together through
	// the states below — the card owns the colour, they inherit it.
	${StyledDescription} {
		color: ${props => props.theme.card.subtitle};
	}

	// An image runs edge to edge, so the card gives up its own padding and the
	// text block underneath takes it instead. Nothing else about the card moves.
	${({ $hasImage, $size }) => $hasImage && css`
		padding: 0;
		gap: 0;
		overflow: hidden;
		align-items: stretch;
		justify-content: flex-start;

		${StyledBody} {
			padding: ${$size === 'large' ? '24px' : '8px 12px'};
		}
	`}

	// Selected wears the same ring as a focused Input or an open dropdown, not a
	// solid green border. Same 1px stroke as resting, so nothing shifts.
	${({ $selected, theme }) => $selected && css`
		${fieldFocusRing};
		color: ${theme.text.primary};

		${StyledDescription} {
			color: ${theme.card.color};
		}
	`}

	// Unselected answers the pointer the way an outline Button does: the label
	// rests dimmed and the whole card — text, graphic and stroke — brightens
	// together under the pointer.
	${({ $selected, theme }) => !$selected && css`
		&:hover,
		&:active {
			color: ${theme.text.primary};
			border-color: ${theme.border.active};

			${StyledDescription} {
				color: ${theme.card.color};
			}
		}

		// Held down, the text stays bright but the stroke drops back to its
		// resting colour and thickens instead, so the card reads as pushed in.
		// The extra 0.5px is an inset shadow rather than a wider border, which
		// would grow the box and shift the row.
		&:active {
			border-color: ${theme.card.default.border};
			box-shadow: inset 0 0 0 0.5px ${theme.card.default.border};
		}
	`}

	// Outranks selected: a disabled card says the same thing whether or not it
	// happens to be the current choice.
	${({ $disabled, theme }) => $disabled && css`
		&, &:hover, &:active {
			cursor: not-allowed;
			opacity: ${theme.form.disabled.opacity};
		}
	`}
`;

export const CardOption = withCssSelector(({
	value,
	label,
	description,
	graphic,
	image,
	selected,
	disabled,
	orientation = 'vertical',
	size = 'default',
	onSelected,
}: {
	value: string,
	label: string | React.ReactNode,
	description?: string | React.ReactNode,
	graphic?: React.ReactNode;
	/** Full-bleed picture above the label. */
	image?: string;
	selected?: boolean,
	disabled?: boolean,
	orientation?: CardOptionOrientation,
	size?: CardOptionSize,
	onSelected: (value: string) => void,
}) => {
	const select = () => {
		if (disabled) return;
		onSelected(value);
	};
	return <StyledCard
		onClick={select}
		// The card is the control, so it has to answer the keyboard like one.
		onKeyDown={event => {
			if (event.key !== 'Enter' && event.key !== ' ') return;
			event.preventDefault();
			select();
		}}
		role="radio"
		aria-checked={Boolean(selected)}
		aria-disabled={Boolean(disabled)}
		tabIndex={disabled ? -1 : 0}
		$selected={selected}
		$disabled={disabled}
		$orientation={orientation}
		$size={size}
		$hasImage={Boolean(image)}
	>
		{/* Decorative: the label beside it is what names the option. */}
		{image && <StyledImage src={image} alt="" />}
		{graphic}
		{description || image
			? <StyledBody>
				<StyledTitle>{label}</StyledTitle>
				{description && <StyledDescription>{description}</StyledDescription>}
			</StyledBody>
			: label}
	</StyledCard>;
}, StyledCard.toString());
